import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { TradeIdea, TradeIdeaDetail as Detail, TradeIdeaQuery } from '../api/client'
import { usePageActions, usePageTitle } from '../AppLayout'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useListQuery } from '../hooks/useListQuery'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import type { Sicht } from '../hooks/ideenRegeln'
import { fussAus, gruppiere, kennzahlenAus, reiheAus, zeileAus }
  from '../hooks/ideenRegeln'
import { Button } from '../components/common/Button'
import { Input, Select, Toggle } from '../components/common/Controls'
import { ErrorBox, Loading } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Umschalter } from '../components/common/Umschalter'
import { IdeenKennzahlen } from '../components/ideen/IdeenKennzahlen'
import { IdeenTafel } from '../components/ideen/IdeenTafel'
import { CLASSIFICATION, STATUS } from './tradeIdeaVocabulary'
import { TradeIdeaDetail } from './TradeIdeaDetail'
import { AblehnenDialog, AnnehmenDialog } from './TradeIdeaDialogs'

const PERMISSION = 'trade_idea:read'
//: Annehmen und Ablehnen an der Zeile: dasselbe Recht wie im Ausklapp.
const WRITE_PERMISSION = 'trade_idea:write'

/**
 * Wie viele Ideen auf einmal kommen.
 *
 * Alle, nicht seitenweise: Die Tafel ordnet nach dem Abstand zum Einstieg,
 * und der lässt sich nur über den ganzen Bestand ordnen. Es sind Dutzende,
 * keine Tausende — die Ideen eines Analysten, keine Kursliste.
 */
const LADUNG = 200

const SICHTEN: { wert: Sicht; text: string }[] = [
  { wert: 'abstand', text: 'Nach Abstand' },
  { wert: 'status', text: 'Nach Status' },
  { wert: 'instrument', text: 'Nach Instrument' },
  { wert: 'branche', text: 'Nach Branche' },
]

/**
 * Die Handelsideen als Tafel mit Kursleiter.
 *
 * Jede Idee zwei Zeilen: Instrument, Vorschlag mit Einstufung und Status,
 * rechts der Abstand zum Einstieg — darunter die Kursleiter mit widerlegt,
 * Zone, nicht nachlaufen und Kurs. Ein Klick klappt die Idee auf: Warum,
 * Stufen, Ziele, Position mit den Knöpfen, Orders, Eckdaten, Chronik,
 * Belege. Geordnet nach Abstand, Status oder Instrument; die Filter laufen
 * am Server und stehen in der Adresse, Sicht und Klappen nicht.
 *
 * Annehmen und Ablehnen gehen auch OHNE Aufklappen: die zwei Zeichen rechts
 * an der Zeile. Ihre Dialoge gehören deshalb der Seite, nicht dem Detail —
 * und das Ablehnen fragt immer nach der Begründung.
 *
 * Eine Idee ist eine Regel mit Kurs, keine Order. Was gekauft wird, steht
 * im aufgeklappten Eintrag — gebucht im Depot, von Hand, nicht vom Agenten.
 */
export function TradeIdeasPage() {
  const allowed = useHasPermission(PERMISSION)
  const darfSchreiben = useHasPermission(WRITE_PERMISSION)
  const [sicht, setSicht] = useState<Sicht>('abstand')
  // Der Dialog zu einem Zeichen an der Zeile, und ein Zähler, der nach dem
  // Schreiben die aufgeklappten Details neu laden lässt.
  const [dialog, setDialog] = useState<{ art: 'annehmen' | 'ablehnen'; idee: TradeIdea } | null>(null)
  const [stand, setStand] = useState(0)
  // Welche Ideen aufgeklappt sind, welche Gruppen zu, und welche Idee die
  // Tastatur gerade hält. Alles Zustand dieses Augenblicks, nicht der
  // Adresse: Man teilt eine Sicht, keine aufgeklappte Zeile.
  const [offen, setOffen] = useState<ReadonlySet<number>>(new Set())
  const [zu, setZu] = useState<ReadonlySet<string>>(new Set())
  const [aktiv, setAktiv] = useState<number | null>(null)
  // Die geladenen Details, damit die Leiter in der Zeile die Ziele bekommt.
  const [details, setDetails] = useState<Record<number, Detail>>({})

  const list = useListParams({
    // `currency` steht NICHT dabei: Ein Filter entscheidet, WELCHE Ideen
    // kommen — die Zielwährung nur, wie ihre Beträge aussehen.
    filters: { q: '', classification: '', status: '', beendete: '' },
    sort: 'created',
    descending: true,
  })

  const { waehrung, wahl } = useZielwaehrung(allowed)

  // Rechts in der Kopfzeile: die Ordnung und die Zielwährung. Beides ändert
  // die Darstellung, keins die Auswahl.
  usePageActions(useMemo(() => (
    <>
      <Umschalter optionen={SICHTEN} wert={sicht} onChange={setSicht} label="Ordnung der Tafel" />
      {wahl}
    </>
  ), [sicht, wahl]))

  const params: TradeIdeaQuery = {
    q: list.filters.q.trim() || undefined,
    classification: list.filters.classification || undefined,
    status: list.filters.status || undefined,
    // Beendete nur auf Wunsch: Die Tafel zeigt, was noch gilt.
    open_only: list.filters.beendete ? undefined : true,
    currency: waehrung ? [waehrung] : undefined,
    sort: 'created', dir: 'desc', size: LADUNG, total: true,
  }

  const { result, loading, error, denied, reload }
    = useListQuery(api.tradeIdeas, params, allowed)

  const zeilen = useMemo(() => (result?.items ?? []).map((i) => zeileAus(i, waehrung)), [result, waehrung])
  const gruppen = useMemo(() => gruppiere(zeilen, sicht), [zeilen, sicht])
  const reihe = useMemo(() => reiheAus(gruppen, zu), [gruppen, zu])
  const kennzahlen = useMemo(() => kennzahlenAus(zeilen), [zeilen])

  // Kurz, weil rechts daneben Ordnung, Waehrung und Suche stehen; der Satz
  // dazu steht bei den Kennzahlen.
  usePageTitle('Handelsideen', result
    ? `${zeilen.length} von ${result.total ?? zeilen.length} Ideen` : undefined)

  function geschrieben() {
    setDialog(null)
    setStand((n) => n + 1)
    reload()
  }

  function klappe(id: number) {
    setAktiv(id)
    setOffen((jetzt) => {
      const naechste = new Set(jetzt)
      if (naechste.has(id)) naechste.delete(id)
      else naechste.add(id)
      return naechste
    })
  }

  // ↑↓ wählen — außer in einem Eingabefeld, dort bewegen die Pfeiltasten
  // den Cursor. Enter klappt die gewählte Zeile: Die Tafel gibt ihr den
  // Fokus, und ihr eigener Tastendruck klappt sie.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const ziel = event.target as HTMLElement | null
      if (ziel && /INPUT|TEXTAREA|SELECT/.test(ziel.tagName)) return
      if (reihe.length === 0) return
      const index = aktiv === null ? -1 : reihe.indexOf(aktiv)
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setAktiv(reihe[(index + 1 + reihe.length) % reihe.length])
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setAktiv(reihe[(index - 1 + reihe.length) % reihe.length])
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [reihe, aktiv])

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Handelsideen" />
  }

  return (
    <>
      <IdeenKennzahlen {...kennzahlen} hinweis="eine Idee ist eine Regel mit Kurs, keine Order" />

      <FilterBar>
        <FilterGrow>
          <Input label="Suche"
                 placeholder="Titel, Begründung oder ISIN — mindestens zwei Zeichen"
                 // OHNE autoFocus: Mit dem Cursor im Suchfeld bewegten die
                 // Pfeiltasten den Cursor statt der Wahl — und Blättern ist
                 // hier der häufigere Griff.
                 value={list.filters.q}
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <Select label="Einstufung" value={list.filters.classification}
                  onChange={(event) => list.setFilter('classification', event.target.value)}>
            <option value="">jede Einstufung</option>
            {Object.entries(CLASSIFICATION).map(([wert, wort]) => (
              <option key={wert} value={wert}>{wort}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Select label="Status" value={list.filters.status}
                  onChange={(event) => list.setFilter('status', event.target.value)}>
            <option value="">jeder Status</option>
            {Object.entries(STATUS).map(([wert, wort]) => (
              <option key={wert} value={wert}>{wort}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterEnd>
          <Toggle on={Boolean(list.filters.beendete)}
                  onChange={(on) => list.setFilter('beendete', on ? '1' : '')}>
            beendete zeigen
          </Toggle>
          <Button onClick={list.reset} disabled={!list.touched}>Zurücksetzen</Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      {loading && !result ? (
        <Loading>lädt Handelsideen …</Loading>
      ) : (
        <IdeenTafel gruppen={gruppen} offen={offen} aktiv={aktiv} zu={zu}
                    onToggle={klappe}
                    onToggleGruppe={(key) => setZu((jetzt) => {
                      const naechste = new Set(jetzt)
                      if (naechste.has(key)) naechste.delete(key)
                      else naechste.add(key)
                      return naechste
                    })}
                    onAlleGruppen={(auf) => setZu(auf ? new Set() : new Set(gruppen.map((g) => g.key)))}
                    onAlleIdeen={(auf) => setOffen(auf ? new Set(zeilen.map((z) => z.id)) : new Set())}
                    onAnnehmen={darfSchreiben ? (z) => setDialog({ art: 'annehmen', idee: z.idee }) : undefined}
                    onAblehnen={darfSchreiben ? (z) => setDialog({ art: 'ablehnen', idee: z.idee }) : undefined}
                    ziele={(id) => details[id]?.targets.map((t) => t.price)}
                    umrechnung={waehrung}
                    detail={(z) => (
                      // Die Zielwährung geht MIT ins Detail: Sonst stünde die
                      // Zeile in Euro und der Ausklapp darunter in Dollar.
                      <TradeIdeaDetail id={z.id} currency={waehrung} stand={stand} onGeaendert={reload}
                                       onGeladen={(d) => setDetails((alt) =>
                                         alt[d.id] === d ? alt : { ...alt, [d.id]: d })} />
                    )}
                    leer={list.touched || list.filters.beendete
                      ? 'Keine Idee in dieser Sicht.'
                      : 'Keine Handelsideen. Ein Analyst trägt Vorschläge über den MCP-Zugang ein — solange keiner gelaufen ist, bleibt die Tafel leer.'}
                    fuss={fussAus(zeilen, waehrung)} />
      )}

      {/* Bedingt gerendert, damit jeder Dialog leer beginnt. */}
      {dialog?.art === 'annehmen' && (
        <AnnehmenDialog idee={dialog.idee} currency={waehrung} onFertig={geschrieben}
                        onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'ablehnen' && (
        <AblehnenDialog idee={dialog.idee} currency={waehrung} onFertig={geschrieben}
                        onAbbrechen={() => setDialog(null)} />
      )}
    </>
  )
}
